import { z } from 'zod';

export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Cursor pagination query. The cursor is opaque to clients (base64url of the last sort key)
 * so list endpoints never expose offsets that invite unbounded scans.
 */
export const pageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  cursor: z
    .string()
    .max(512)
    .regex(/^[A-Za-z0-9_-]+$/, 'invalid cursor')
    .optional(),
});
export type PageQuery = z.infer<typeof pageQuerySchema>;

export function pageResponseSchema<Item extends z.ZodType>(item: Item) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Cursor for lists keyed by a time-ordered UUIDv7 id. Built on `btoa`/`atob` rather than
 * `Buffer`, so the admin apps share these helpers in the browser.
 */
export function encodeIdCursor(id: string): string {
  return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Returns the id inside a cursor, or undefined for anything that is not one of ours. */
export function decodeIdCursor(cursor: string | undefined): string | undefined {
  if (cursor === undefined) return undefined;
  const base64 = cursor.replace(/-/g, '+').replace(/_/g, '/');
  let id: string;
  try {
    id = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
  } catch {
    // Not base64 at all: treat it like any other foreign cursor.
    return undefined;
  }
  return UUID_PATTERN.test(id) ? id : undefined;
}
